/**
 * The Traces tab's Logs and Metrics views (vfs-service src/traces/signalRoutes.js, in the person's sandbox pod): the log
 * records and metrics the project's services export over OpenTelemetry, next to their spans. Same base URL, authorized
 * fetch, ACL headers and errors as TracesService.js / DataService.js. Owner, editor and viewer may all read.
 */
import config from '../config';
import { getAuthorizedFetch } from '../utils/fetchRegistry';
import { buildAclContextHeaders } from '../utils/aclContext';
import { DataError, fail } from './DataService';

const base = () => (config.vfsServiceUrl || '').replace(/\/$/, '');
const telemetryUrl = (projectId, path) => `${base()}/api/v1/projects/${encodeURIComponent(projectId)}/telemetry${path}`;

const query = (params) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') { search.set(key, String(value)); }
  });
  const text = search.toString();
  return text ? `?${text}` : '';
};

async function get(url) {
  if (!base()) { throw new DataError('vfs-service is not configured', 0, null); }
  const response = await getAuthorizedFetch()(url, { method: 'GET', headers: { Accept: 'application/json', ...buildAclContextHeaders() } });
  if (!response.ok) { throw await fail(response); }
  return (await response.json().catch(() => null)) || {};
}

/**
 * The log records of the range, newest first: `{ enabled, reason?, logs: [{ id, time, severity, body, service, traceId,
 * spanId, attributes }], total, next, services: [{ name, count }], levels: { trace, debug, info, warn, error, fatal } }`.
 * `severity`: that level and above; `q`: text in the message or an attribute; `trace`: one request's records;
 * `cursor`: the `next` of the previous page.
 */
export function listLogs(projectId, { since, until, service, severity, q, trace, limit = 100, cursor } = {}) {
  return get(telemetryUrl(projectId, `/logs${query({ since, until, service, severity, q: q ? q.trim() : undefined, trace, limit, cursor })}`));
}

/** The metric names: `{ enabled, reason?, metrics: [{ name, unit, kind, monotonic, services, series, lastAt }] }`. */
export function listMetrics(projectId, { service, q } = {}) {
  return get(telemetryUrl(projectId, `/metrics${query({ service, q })}`));
}

/**
 * One metric's series in the range: `{ enabled, name, since, until, total, series: [{ service, attributes, unit, kind,
 * valueIs: 'value' | 'increase' | 'average', points: [{ t, value }], summary: { count, min, max, last, ... } }] }`.
 */
export function getMetricSeries(projectId, { name, service, since } = {}) {
  return get(telemetryUrl(projectId, `/metrics/series${query({ name, service, since })}`));
}
